import React, { useEffect, useMemo, useRef, useState } from 'react';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, IconButton, Typography } from '@mui/material';
import {
  buildMergedTocPreviewEntries,
  buildMergedTocPreviewLines,
  normalizeTocNumbering,
  tocDisplayTitle,
} from './subMatTemplateTocOrder';

export { buildMergedTocPreviewLines, tocDisplayTitle };

/** PDF A4 / SubMatMergePdfService 목차 배율 기준 (pt) */
const A4_WIDTH_PT = 595.27;
const TOC_TITLE_PT = 24;
const TOC_MARGIN_X_PT = 36;
const TOC_MARGIN_TOP_PT = 72;
const TOC_RULE_GAP_PT = 8;
const TOC_AFTER_RULE_PT = 22;

const ZOOM_MIN = 0.75;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;

const fontSizePtForDepth = (depth) => {
  if (depth <= 0) return 13;
  if (depth === 1) return 11;
  return 10;
};

const leadingPtForDepth = (depth) => {
  if (depth <= 0) return 22;
  if (depth === 1) return 17;
  return 15;
};

const indentPtForDepth = (depth) => (
  depth <= 0 ? 0 : 12 + (depth - 1) * 14
);

const SubMatTemplateTocPreview = ({
  items,
  tocNumbering,
  extraAttachments,
  extraSectionTitle,
  fillHeight = false,
  bare = false,
  /** 회색 바탕 위 흰 문서 테두리 (템플릿 편집 목차 패널용) */
  documentLook = false,
}) => {
  const style = normalizeTocNumbering(tocNumbering);
  const entries = useMemo(
    () => buildMergedTocPreviewEntries(items, style, extraAttachments, extraSectionTitle),
    [items, style, extraAttachments, extraSectionTitle],
  );
  const empty = entries.length === 0;

  const viewportRef = useRef(null);
  const [viewportSize, setViewportSize] = useState({ w: 0, h: 0 });
  /** 1 = 칸 너비에 맞춤. 세로는 스크롤 */
  const [zoomFactor, setZoomFactor] = useState(1);

  useEffect(() => {
    if (!documentLook) return undefined;
    const el = viewportRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h <= 0) return;
      setViewportSize((prev) => (
        Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1 ? prev : { w, h }
      ));
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [documentLook]);

  const pageWidthPx = viewportSize.w > 0 ? viewportSize.w * zoomFactor : 0;
  const scale = pageWidthPx > 0 ? pageWidthPx / A4_WIDTH_PT : 0;
  const pt = (n) => (scale > 0 ? n * scale : undefined);

  const bareBody = (
    <>
      <Typography
        align="center"
        sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 2, fontFamily: 'NanumGothic, "Malgun Gothic", sans-serif' }}
      >
        목차
      </Typography>
      {empty ? (
        <Typography variant="body2" color="text.secondary">
          목차를 구성하면 여기에 미리보기가 표시됩니다.
        </Typography>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            fontFamily: 'NanumGothic, "Malgun Gothic", "Noto Sans KR", monospace',
            fontSize: '0.8125rem',
            lineHeight: 1.65,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {buildMergedTocPreviewLines(items, style, extraAttachments, extraSectionTitle).join('\n')}
        </Box>
      )}
    </>
  );

  if (documentLook) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: fillHeight ? '100%' : undefined,
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.25,
            flexShrink: 0,
            px: 0.5,
            pb: 0.5,
          }}
        >
          <IconButton
            size="small"
            aria-label="축소"
            disabled={zoomFactor <= ZOOM_MIN}
            onClick={() => setZoomFactor((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100))}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="확대"
            disabled={zoomFactor >= ZOOM_MAX}
            onClick={() => setZoomFactor((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100))}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          ref={viewportRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: zoomFactor > 1 ? 'auto' : 'hidden',
            overflowY: 'auto',
            display: 'flex',
            p: '2px',
            boxSizing: 'border-box',
            // 확대 시 center면 왼쪽 오버플로우가 스크롤로 닿지 않음
            justifyContent: zoomFactor > 1 ? 'flex-start' : 'center',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              width: zoomFactor === 1 || pageWidthPx <= 0 ? '100%' : pageWidthPx,
              maxWidth: zoomFactor > 1 && pageWidthPx > 0 ? pageWidthPx : '100%',
              aspectRatio: '210 / 297',
              flexShrink: 0,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 0,
              boxShadow: 'none',
              boxSizing: 'border-box',
              px: scale > 0 ? `${pt(TOC_MARGIN_X_PT)}px` : 2.5,
              pt: scale > 0 ? `${pt(TOC_MARGIN_TOP_PT)}px` : 2.5,
              pb: scale > 0 ? `${pt(56)}px` : 2.5,
            }}
          >
            <Typography
              align="center"
              sx={{
                fontWeight: 700,
                fontSize: scale > 0 ? `${pt(TOC_TITLE_PT)}px` : '1.25rem',
                mb: scale > 0 ? `${pt(TOC_RULE_GAP_PT)}px` : 1,
                fontFamily: 'NanumGothic, "Malgun Gothic", sans-serif',
                lineHeight: 1.15,
              }}
            >
              목차
            </Typography>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'rgba(0,0,0,0.45)',
                mb: scale > 0 ? `${pt(TOC_AFTER_RULE_PT)}px` : 2,
              }}
            />
            {empty ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: scale > 0 ? `${pt(11)}px` : undefined }}
              >
                목차를 구성하면 여기에 미리보기가 표시됩니다.
              </Typography>
            ) : (
              <Box sx={{ fontFamily: 'NanumGothic, "Malgun Gothic", "Noto Sans KR", sans-serif' }}>
                {entries.map((e, i) => {
                  if (e.sectionGap) {
                    return <Box key={`gap-${i}`} sx={{ height: scale > 0 ? `${pt(10)}px` : 10 }} />;
                  }
                  const fs = fontSizePtForDepth(e.depth);
                  const lh = leadingPtForDepth(e.depth);
                  const ind = indentPtForDepth(e.depth);
                  return (
                    <Box
                      key={`row-${i}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 0.5,
                        fontSize: scale > 0 ? `${pt(fs)}px` : `${fs}px`,
                        lineHeight: scale > 0 ? `${pt(lh)}px` : `${lh}px`,
                        pl: scale > 0 ? `${pt(ind)}px` : `${ind}px`,
                        color: e.depth <= 0 ? 'text.primary' : 'rgba(0,0,0,0.85)',
                        minWidth: 0,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 1,
                          minWidth: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {e.text}
                      </Box>
                      {e.showPageHint && (
                        <>
                          <Box
                            aria-hidden
                            sx={{
                              flex: 1,
                              minWidth: 12,
                              alignSelf: 'center',
                              borderBottom: '1px dotted',
                              borderColor: 'rgba(0,0,0,0.45)',
                              transform: 'translateY(-0.15em)',
                            }}
                          />
                          <Box
                            component="span"
                            sx={{
                              flexShrink: 0,
                              color: 'text.secondary',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            xx
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...(fillHeight
          ? {
              height: '100%',
              minHeight: 200,
              overflow: 'auto',
              boxSizing: 'border-box',
            }
          : {
              height: empty ? 200 : 'auto',
              minHeight: 200,
              overflow: empty ? 'hidden' : 'visible',
            }),
        ...(bare
          ? {}
          : {
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50',
            }),
        p: 2,
      }}
    >
      {bareBody}
    </Box>
  );
};

export default SubMatTemplateTocPreview;
